(ns hai.account.credits.routes
  (:require
   [clojure.string :as str]
   [hai.ui.components :as c]
   [hai.ui.components.card :as card]
   [hai.ui.components.collection.filters :as filters]
   [hai.ui.components.collection.table :as collection]
   [hai.ui.components.collection.views.cards :as cards]
   [hai.ui.components.collection.views.table :as table]
   [hai.ui.components.form.select.single-select :as single-select]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e defevent-fx defsub]]
   [hai.web-app.layout :as layout]
   [hai.web-app.routing :as routing]
   [lambdaisland.deja-fu :as fu]
   [lambdaisland.ornament :as o]
   [reagent.core :as r]))

(defevent-fx :credits/load-credit-history
  [_ {:keys [user-uuid limit offset]}]
  {:fetch {:url "/api/credits/v1/history"
           :query-params (cond-> {}
                           limit (assoc :limit limit)
                           offset (assoc :offset offset))
           :transform-fn :transactions
           :store-in [:credits/credit-history]}})

#_(defevent-fx :credits/load-all-transactions
    [_]
    {:fetch {:url "/api/credits/v1/admin/transactions"
             :store-in [:credits/all-transactions]}})

(defsub :credits/credit-history [db]
  (reverse (sort-by :credit-transaction/timestamp (:credits/credit-history db))))

#_(defsub :credits/all-transactions [db]
    (:credits/all-transactions db))

(defn format-timestamp
  "Format a timestamp for display"
  [ts]
  (if ts
    [:span {:title (str ts)}
     (fu/distance-in-words (fu/local-date-time) (goog.date.DateTime. ts))
     " ago"]
    "—"))

(defn format-amount
  "Format credit amount with color coding"
  [amount type]
  (let [color (cond
                (str/includes? (str type) "debit") "text-red-600"
                (str/includes? (str type) "credit") "text-green-600"
                (pos? amount) "text-green-600"
                :else "text-red-600")]
    [:span {:class color}
     (if (pos? amount) "+" "")
     amount " credits"]))

(o/defstyled transaction-type-badge :div
  ([type]
   (let [color (case (keyword type)
                 :grant :green
                 :deduct :red
                 :adjustment :yellow
                 :purchase :green
                 :refund :orange
                 :usage :blue
                 :gray)]
     [c/Badge {:color (name color) :radius "full" :variant "solid"}
      (name type)])))

(defn credit-history-table
  "Table component for displaying credit history"
  []
  (r/with-let [_ (>e [:credits/load-all-transactions])]
    [:div
     [c/Heading {:size "4"} "Transactions"]
     [:div.mt-4
      [collection/component
       {:id ::table
        ;; :rows-sub [:credits/credit-history]
        :query [{:credit-transactions/table
                 [{:collection/items [:credit-transaction/uuid
                                      :credit-transaction/amount
                                      :credit-transaction/user
                                      :credit-transaction/type
                                      :credit-transaction/description
                                      :credit-transaction/timestamp
                                      :credit-transaction/metadata]}]}]
        ;; TODO default card render
        :card (fn [collection item]
                [card/card
                 [cards/card-select-checkbox collection item]
                 [cards/column-render collection item]])
        :columns [{:header "Timestamp"
                   :attr   :credit-transaction/timestamp
                   :sort?  false
                   :render (fn [transaction]
                             [table/table-cell
                              (format-timestamp (:credit-transaction/timestamp transaction))])}
                  {:header "Type"
                   :sort?  false
                   :attr   :credit-transaction/type
                   :filter {:schema      :keyword
                            :type        :filter.remote/select
                            :placeholder "Type..."
                            :field       single-select/field}
                   :render (fn [transaction]
                             [table/table-cell
                              [transaction-type-badge (:credit-transaction/type transaction)]])}
                  {:header "Amount"
                   :attr   :credit-transaction/amount
                   :sort?  false
                   :render (fn [transaction]
                             [table/table-cell
                              (format-amount (:credit-transaction/amount transaction)
                                             (:credit-transaction/type transaction))])}
                  {:header "Description"
                   :attr   :credit-transaction/description
                   :sort?  false
                   :filter (filters/text-search {:placeholder "Title..."})
                   :render (fn [transaction]
                             [table/table-cell
                              (or (:credit-transaction/description transaction) "—")])}
                  #_{:header "Metadata"
                     :attr   :credit-transaction/metadata
                     :render (fn [transaction]
                               [table/table-cell
                                (if-let [metadata (:credit-transaction/metadata transaction)]
                                  [:span.text-xs.text-gray-600
                                   (if (:issued-by metadata)
                                     (str "Issued by: " (:issued-by metadata))
                                     (pr-str metadata))]
                                  "—")])}]
        :paginate? true
        :pagination {:direction :desc
                     #_#_:sort-attrs [:credit-transaction/timestamp]}}]]]))

#_(defn summary-stats
    "Display summary statistics for credit transactions"
    []
    (let [transactions @(<s :credits/all-transactions)]
      (when (seq transactions)
        (let [total-granted (->> transactions
                                 (filter #(pos? (:credits/amount %)))
                                 (map :credits/amount)
                                 (reduce + 0))
              total-deducted (->> transactions
                                  (filter #(neg? (:credits/amount %)))
                                  (map :credits/amount)
                                  (reduce + 0))]
          [:div.mb-6.grid.grid-cols-3.gap-4
           [:div.bg-gray-50.p-4.rounded
            [c/Text {:size "sm" :muted true} "Total Transactions"]
            [c/Heading {:size "md"} (count transactions)]]
           [:div.bg-green-50.p-4.rounded
            [c/Text {:size "sm" :muted true} "Total Credits Granted"]
            [c/Heading {:size "md" :class "text-green-600"}
             (str "+" total-granted)]]
           [:div.bg-red-50.p-4.rounded
            [c/Text {:size "sm" :muted true} "Total Credits Deducted"]
            [c/Heading {:size "md" :class "text-red-600"}
             (str total-deducted)]]]))))

(o/defstyled balance-card :div
  :bg-gradient-to-r :text-white
  :from-purple-500 :via-indigo-500 :to-blue-500
  :max-w-xs
  :rounded-lg
  :p-4
  ([amount]
   [:<>
    [c/Flex {:gap "1" :direction "column"}
     [c/Text {:size "3"} "Credit Balance"]
     [c/Text {:size "5" :weight "bold"} amount]]]))

(defn paid-enterprise-email? [email]
  (some #(str/ends-with? email %)
        #{"@ism.lt" "@purple.ai" "@socialvalueportal.com"}))

(defn page
  [_]
  (r/with-let [!identity (<s :auth/identity)]
    [layout/page
     [layout/breadcrumbs]
     [layout/heading]
     [layout/lead*
      [layout/lead
       [:span
        [:strong "View and track all credit transactions across the platform"]
        [:br]
        "Monitor credit grants, deductions, purchases, and adjustments for your account."]]
      [:div.buttons
       [c/Box {:mb "4"}
        (let [{:user/keys [credits]} @!identity]
          [balance-card (if (paid-enterprise-email? (:user/email @!identity))
                          "∞"
                          credits)])]]]
     [layout/content
      [credit-history-table]]]))

(defn routes []
  [["/account/credits"
    {:name :account/credits
     :label "Credits"
     :icon icons/outlined-payments
     :view page
     ::routing/window-title "Credits"
     :controllers [{:start (fn []
                             (>e [:credits/load-credit-history]))}]}]])
